import * as React from 'react'
import { ForwardedRef, ReactElement, useEffect, useState } from 'react'
import { Input, InputProps, makeStyles, mergeClasses, Text } from '@fluentui/react-components'
import { formatDecimals } from '../../helpers/numberHelper'
import { usePrevious } from '@fluentui/react-hooks'

export enum CommitEventType {
    OnLoad = 'ONLOAD',
    OnSubmit = 'ONSUBMIT',
    OnBlur = 'ONBLUR',
}

export type FormattableNumberInputProps = Omit<InputProps, 'onChange' | 'onFocus' | 'onBlur' | 'value'> & {
    allowNegative?: boolean
    decimals?: number
    symbol?: string
    value?: string | number
    onCommit?: (eventType: CommitEventType, value: string) => void
}

const useStyles = makeStyles({
    numberInput: { minWidth: 0, '> input': { textAlign: 'right' } },
})

// Do some preprocessing during the inputting of data to allow for more flexibility by the user.
const formatValue = (value: string, allowNegative?: boolean): string =>
    value
        // Only allow the first character to be a negative sign.
        .replace(allowNegative ? /([\d.]+)(-)/g : /(-)/g, (match, firstGroup) => (allowNegative ? firstGroup : ''))
        // Only accept numbers, commas, dots and negative signs.
        .replace(/[^-\d.,]/, '')
        // Reject any thousand separators.
        .replace(/[.,](?=\d*[.,])/, '')
        // Use comma as decimal separator. This must be variable when introducing other notations.
        .replace('.', ',')

function FormattableNumberInput(props: FormattableNumberInputProps, ref: ForwardedRef<HTMLInputElement>): ReactElement {
    const styles = useStyles()
    const [displayValue, setDisplayValue] = useState<string>()
    const previousDisplayValue = usePrevious(displayValue)

    const onCommit = (eventType: CommitEventType) => () => {
        props.onCommit?.(eventType, formatDecimals(displayValue, props.decimals ?? 0, 'nl-nl', 'en', false))
    }

    useEffect(() => {
        setDisplayValue(formatDecimals(props.value, props.decimals ?? 0, 'en', 'nl-nl', true))
    }, [props.value])

    useEffect(() => {
        if (previousDisplayValue === undefined && displayValue !== undefined) {
            onCommit?.(CommitEventType.OnLoad)
        }
    }, [displayValue])

    return (
        <Input
            contentBefore={props.symbol && <Text>{props.symbol}</Text>}
            {...props}
            ref={ref}
            className={mergeClasses(styles.numberInput, props.className)}
            value={displayValue || ''}
            onChange={(_, data) => setDisplayValue(formatValue(data.value, props.allowNegative))}
            onFocus={event => event.target.select()}
            onBlur={onCommit(CommitEventType.OnBlur)}
            onSubmit={onCommit(CommitEventType.OnSubmit)}
        />
    )
}

export default React.forwardRef(FormattableNumberInput)
